import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { useCippTenants, type CippTenantListRow } from '@/api/cipp';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cippTenantPath, ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { OrbitLink } from '@/components/insights/OrbitLink';

/**
 * CIPP → Tenants. Every Microsoft 365 tenant CIPP manages, with its mapped Autotask
 * company and whether CIPP can reach it. Served from the server's 15-minute tenant
 * cache, so it costs CIPP nothing per viewer. Click a tenant for its users.
 */

type Show = 'all' | 'unmapped' | 'attention';

/** GDAP relationships ending within this many days (and not auto-extending) get flagged. */
const GDAP_WARN_DAYS = 30;

function gdapDaysLeft(t: CippTenantListRow): number | null {
  return t.gdapExpires ? differenceInCalendarDays(new Date(t.gdapExpires), new Date()) : null;
}

function needsAttention(t: CippTenantListRow): boolean {
  const days = gdapDaysLeft(t);
  return !t.healthy || (!t.gdapAutoExtend && days != null && days <= GDAP_WARN_DAYS);
}

export default function CippTenantsPage() {
  const tenants = useCippTenants({ scoped: true });
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<Show>('all');

  const rows = tenants.data?.items ?? [];
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((t) => show === 'all' || (show === 'unmapped' ? !t.mapping || t.mapping.ignored : needsAttention(t)))
      .filter((t) => !q || [t.displayName, t.defaultDomainName, t.mapping?.companyName ?? ''].some((s) => s.toLowerCase().includes(q)));
  }, [rows, search, show]);

  const unmapped = rows.filter((t) => !t.mapping || t.mapping.ignored).length;
  const attention = rows.filter(needsAttention).length;

  return (
    <DesktopOnly
      title="CIPP is available on desktop"
      message="Open Cosmos on a larger screen to browse Microsoft 365 tenants."
    >
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
        <PageHeader
          title="Tenants"
          count={tenants.data ? visible.length : null}
          isFetching={tenants.isFetching}
          subtitle={<>CIPP · Microsoft 365 tenants{tenants.data && <> · Updated {fmtRelative(tenants.data.fetchedAt)}</>}</>}
        />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SearchInput withIcon placeholder="Tenant, domain or company…" className="w-72" value={search} onValueChange={setSearch} />
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            {([
              ['all', 'All', rows.length],
              ['attention', 'Needs attention', attention],
              ['unmapped', 'Not mapped', unmapped],
            ] as const).map(([id, label, n]) => (
              <button
                key={id}
                type="button"
                onClick={() => setShow(id)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                  show === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label} <span className="ml-1 tabular-nums text-muted-foreground">{n}</span>
              </button>
            ))}
          </div>
        </div>

        {tenants.error && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load tenants: {tenants.error instanceof Error ? tenants.error.message : 'Unknown error'}
          </div>
        )}

        {tenants.isLoading && <div className="py-10 text-center text-sm text-muted-foreground">Loading tenants from CIPP…</div>}

        {tenants.data && (
          <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5">Tenant</th>
                  <th className="px-4 py-2.5">Autotask company</th>
                  <th className="px-4 py-2.5">CIPP access</th>
                  <th className="px-4 py-2.5">GDAP</th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-right">CIPP refresh</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {visible.map((t) => {
                  const days = gdapDaysLeft(t);
                  const gdapWarn = !t.gdapAutoExtend && days != null && days <= GDAP_WARN_DAYS;
                  return (
                    <tr
                      key={t.customerId}
                      onClick={() => navigate(cippTenantPath(t.customerId))}
                      className="cursor-pointer hover:bg-muted/30"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{t.displayName}</div>
                        <div className="text-xs text-muted-foreground">{t.defaultDomainName}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        {t.mapping && !t.mapping.ignored
                          ? <OrbitLink companyId={t.mapping.companyId}>{t.mapping.companyName}</OrbitLink>
                          : <span className="text-xs text-muted-foreground">{t.mapping?.ignored ? 'Ignored' : 'Not mapped'}</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        {t.healthy
                          ? <span className="whitespace-nowrap rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-medium text-success">Reachable</span>
                          : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive" title={`${t.graphErrorCount} Graph errors`}>
                              <AlertTriangle className="h-3 w-3" /> Unreachable
                            </span>
                          )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                        {days == null ? <span className="text-muted-foreground">—</span> : (
                          <span className={cn(gdapWarn ? 'font-medium text-warning' : 'text-muted-foreground')}>
                            {days < 0 ? 'Expired' : `Ends in ${days} day${days !== 1 ? 's' : ''}`}
                            {t.gdapAutoExtend && ' · auto-extends'}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs text-muted-foreground">{fmtRelative(t.lastRefresh)}</td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No tenants match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {unmapped > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            Unmapped tenants don't show a Microsoft 365 card on tickets. Admins can link them in{' '}
            <Link to={ROUTES.admin.cipp} className="underline underline-offset-2 hover:text-foreground">Admin → CIPP mapping</Link>.
          </p>
        )}
      </div>
    </DesktopOnly>
  );
}
